import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'wbs_admin_token';

const AuthCtx = createContext({
  token: null,
  username: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(true);

  // Attach Bearer token to all axios requests (removed on logout)
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t && config.url && config.url.includes('/api/')) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${t}`;
        }
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptorId);
  }, []);

  // Verify token on mount / whenever it changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setUsername(null); setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setUsername(res.data?.username || 'admin');
      } catch (e) {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUsername(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const login = async (u, p) => {
    const res = await axios.post(`${API}/auth/login`, { username: u, password: p });
    const t = res.data?.access_token;
    if (!t) throw new Error('No token returned');
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    setUsername(res.data?.username || u);
    return true;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUsername(null);
  };

  return (
    <AuthCtx.Provider value={{ token, username, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
